import { useEffect, useRef, useState } from 'react';
import type { Track, Playlist } from '../page';
import SafeImage from './SafeImage';
import {
  addPlaylistComment,
  deletePlaylistComment,
  getPlaylistComments,
  likePlaylist,
  likeTrack,
  unlikePlaylist,
  unlikeTrack,
  updatePlaylistComment,
} from '../../../api/client';
import type { PlaylistCommentApi } from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import { getMediaType } from '../../../utils/media';
import { CommentEditMeta } from '../../../utils/commentMeta';

interface PlaylistSectionProps {
  currentTrack: Track;
  setCurrentTrack: (track: Track) => void;
  playlist: Playlist;
  onRemoveTrack: (playlistId: number, trackId: number) => void;
  /** When false, hide per-track remove (e.g. viewing someone else's playlist). Default true. */
  canRemoveTracks?: boolean;
  /** If set, track rows call this instead of setCurrentTrack (e.g. navigate to listening room). */
  onTrackSelect?: (track: Track) => void;
  /** Listening room: sync play/pause with bottom player. */
  isPlaying?: boolean;
  /** Toggle play/pause when tapping the active track (listening room only). */
  onTogglePlayback?: () => void;
  /** Home: load track and open YouTube/SoundCloud embed (optional). */
  onPlayVideoEmbed?: (track: Track) => void;
  /** When on, after each direct audio file ends, advance to the next playable track in this list. */
  continuousPlay?: boolean;
  onContinuousPlayChange?: (next: boolean) => void;
  /** Persist new track order (same API as remove — full ordered track id list). */
  onReorderTracks?: (playlistId: number, trackIds: number[]) => void;
}

export default function PlaylistSection({
  currentTrack,
  setCurrentTrack,
  playlist,
  onRemoveTrack,
  canRemoveTracks = true,
  onTrackSelect,
  isPlaying = false,
  onTogglePlayback,
  onPlayVideoEmbed,
  continuousPlay = false,
  onContinuousPlayChange,
  onReorderTracks,
}: PlaylistSectionProps) {
  const { isLoggedIn, username: currentUsername, canModerate, userId } = useAuth();

  const showReorder = Boolean(canRemoveTracks && onReorderTracks && !onTrackSelect && playlist.id);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  /** Some browsers mishandle dataTransfer for index 0; keep source index here as source of truth. */
  const dragSourceIndexRef = useRef<number | null>(null);

  const applyReorder = (fromIndex: number, toIndex: number) => {
    if (!onReorderTracks || !playlist.id) return;
    if (fromIndex === toIndex) return;
    const next = [...playlist.tracks];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    onReorderTracks(
      playlist.id,
      next.map((t) => t.id)
    );
  };

  const sortPlaylistTracks = (key: 'title' | 'artist') => {
    if (!onReorderTracks || !playlist.id || playlist.tracks.length < 2) return;
    const sorted = [...playlist.tracks].sort((a, b) => {
      const va = key === 'title' ? a.title : a.artist;
      const vb = key === 'title' ? b.title : b.artist;
      return va.localeCompare(vb, undefined, { sensitivity: 'base' });
    });
    onReorderTracks(
      playlist.id,
      sorted.map((t) => t.id)
    );
  };

  const pickTrack = (track: Track) => {
    if (onTrackSelect) onTrackSelect(track);
    else setCurrentTrack(track);
  };

  /** Primary control: select track, or toggle play/pause if already selected (home only). */
  const handleTrackActivate = (track: Track) => {
    if (onTrackSelect) {
      onTrackSelect(track);
      return;
    }
    if (currentTrack.id === track.id) {
      onTogglePlayback?.();
    } else {
      setCurrentTrack(track);
    }
  };
  const [comments, setComments] = useState<PlaylistCommentApi[]>([]);
  const [commentBody, setCommentBody] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentBody, setEditCommentBody] = useState('');
  const [editPlaylistModeratorNote, setEditPlaylistModeratorNote] = useState('');

  const canEditPlaylistComment = (commentUserId: number) =>
    canModerate || (typeof userId === 'number' && userId === commentUserId);

  const showPlaylistModeratorNote = () => canModerate;
  const [playlistLiked, setPlaylistLiked] = useState(!!playlist.isLiked);
  const [likedTrackIds, setLikedTrackIds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setPlaylistLiked(!!playlist.isLiked);
    const nextLiked: Record<number, boolean> = {};
    for (const track of playlist.tracks) {
      if (track.isLiked) nextLiked[track.id] = true;
    }
    setLikedTrackIds(nextLiked);
    if (!playlist.id) {
      setComments([]);
      return;
    }
    getPlaylistComments(playlist.id).then(setComments).catch(() => setComments([]));
  }, [playlist.id, playlist.isLiked]);

  const onTogglePlaylistLike = async () => {
    if (!isLoggedIn || !playlist.id) return;
    try {
      if (playlistLiked) await unlikePlaylist(playlist.id);
      else await likePlaylist(playlist.id);
      setPlaylistLiked((prev) => !prev);
    } catch (err) {
      console.error(err);
    }
  };

  const onToggleTrackLike = async (trackId: number) => {
    if (!isLoggedIn) return;
    try {
      if (likedTrackIds[trackId]) {
        await unlikeTrack(trackId);
        setLikedTrackIds((prev) => ({ ...prev, [trackId]: false }));
      } else {
        await likeTrack(trackId);
        setLikedTrackIds((prev) => ({ ...prev, [trackId]: true }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const submitComment = async () => {
    if (!isLoggedIn || !playlist.id || !commentBody.trim()) return;
    try {
      const created = await addPlaylistComment(playlist.id, commentBody.trim());
      setComments((prev) => [created, ...prev]);
      setCommentBody('');
    } catch (err) {
      console.error(err);
    }
  };

  const savePlaylistCommentEdit = async () => {
    if (!playlist.id || editingCommentId == null || !editCommentBody.trim()) return;
    try {
      const row = comments.find((c) => c.id === editingCommentId);
      const showModNote = !!(row && showPlaylistModeratorNote());
      const updated = await updatePlaylistComment(
        playlist.id,
        editingCommentId,
        editCommentBody.trim(),
        showModNote ? { moderator_note: editPlaylistModeratorNote.trim() || null } : undefined
      );
      setComments((prev) => prev.map((c) => (c.id === editingCommentId ? { ...c, ...updated } : c)));
      setEditingCommentId(null);
      setEditCommentBody('');
      setEditPlaylistModeratorNote('');
    } catch (err) {
      console.error(err);
    }
  };

  const removePlaylistComment = async (commentId: number) => {
    if (!playlist.id || !confirm('Delete this comment?')) return;
    try {
      await deletePlaylistComment(playlist.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentBody('');
        setEditPlaylistModeratorNote('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">Now playing</p>
          {currentTrack.id === 0 ? (
            <div className="mt-4 flex items-center gap-4 rounded-[1.5rem] bg-stone-50 p-4">
              <div className="flex h-24 w-24 items-center justify-center rounded-[1.5rem] bg-stone-200 text-stone-400">
                <i className="ri-music-line text-4xl" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-semibold text-stone-900">No track selected</p>
                <p className="mt-1 text-sm leading-6 text-stone-600">
                  Pick a track from the playlist, or start the live radio to fill the room.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex items-center gap-4 rounded-[1.5rem] bg-[linear-gradient(135deg,#1c1917_0%,#44403c_50%,#78350f_100%)] p-4 text-white shadow-sm">
              <SafeImage src={currentTrack.image} alt={currentTrack.title} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-[0.22em] text-amber-200/70">Active track</p>
                <h3 className="mt-2 truncate text-xl font-semibold">{currentTrack.title}</h3>
                <p className="mt-1 truncate text-sm text-stone-200">{currentTrack.artist}</p>
                <p className="truncate text-xs text-stone-300">{currentTrack.album}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-amber-100">
                {currentTrack.duration}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">Selected playlist</p>
          {playlist.id === 0 ? (
            <div className="mt-4 rounded-[1.5rem] bg-stone-50 p-5 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-stone-200 text-stone-500">
                <i className="ri-play-list-line text-2xl" />
              </div>
              <p className="mt-4 text-lg font-semibold text-stone-900">No playlists yet</p>
              <p className="mt-1 text-sm text-stone-600">Create one from Manage or pull in tracks from Discover.</p>
            </div>
          ) : (
            <div className="mt-4">
              <div className="flex items-center gap-4">
                <SafeImage src={playlist.coverImage || ''} alt={playlist.name} size="md" />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-xl font-semibold text-stone-900">{playlist.name}</h2>
                  {playlist.ownerUsername ? (
                    <div className="mt-2 flex items-center gap-2">
                      {playlist.ownerAvatarUrl ? (
                        <img
                          src={playlist.ownerAvatarUrl}
                          alt={playlist.ownerUsername}
                          className="h-8 w-8 rounded-full object-cover ring-2 ring-stone-200 shrink-0"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-800 uppercase ring-2 ring-stone-200">
                          {playlist.ownerUsername[0]}
                        </span>
                      )}
                      <span className="text-sm text-stone-500">
                        By <span className="font-medium text-stone-700">@{playlist.ownerUsername}</span>
                      </span>
                    </div>
                  ) : null}
                  <p className="mt-1 text-sm text-stone-600">{playlist.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium capitalize text-stone-700">
                      {playlist.visibility || 'public'}
                    </span>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                      {playlist.tracks.length} tracks
                    </span>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                      {playlist.likesCount ?? 0} likes
                    </span>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                      {playlist.commentsCount ?? comments.length} comments
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onTogglePlaylistLike}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                    playlistLiked ? 'bg-red-50 text-red-700' : 'bg-stone-900 text-white'
                  }`}
                >
                  <i className={playlistLiked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                  {playlistLiked ? 'Liked' : 'Like playlist'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-semibold text-stone-900">Tracks</h3>
            <p className="text-sm text-stone-500">
              {playlist.id === 0
                ? 'Choose or create a playlist to begin.'
                : onTrackSelect
                  ? 'Tap a track to open it in the listening room.'
                  : `Tap a track to play. Expand the player for the full audio layout; tap the row video icon to watch. ${
                      showReorder && playlist.tracks.length >= 2
                        ? 'Drag the grip handle to reorder.'
                        : ''
                    }`.trim()}
            </p>
            {((!onTrackSelect && playlist.id !== 0 && onContinuousPlayChange) ||
              (showReorder && playlist.tracks.length >= 2)) && (
              <p className="mt-1 text-xs text-stone-400">
                {!onTrackSelect && playlist.id !== 0 && onContinuousPlayChange ? 'Continuous plays the list in order.' : ''}
                {!onTrackSelect && playlist.id !== 0 && onContinuousPlayChange && showReorder && playlist.tracks.length >= 2
                  ? ' '
                  : ''}
                {showReorder && playlist.tracks.length >= 2
                  ? 'Sort by title or artist →'
                  : ''}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {showReorder && playlist.tracks.length >= 2 ? (
              <>
                <button
                  type="button"
                  onClick={() => sortPlaylistTracks('title')}
                  className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:border-amber-200 hover:bg-amber-50/80 hover:text-amber-900"
                  title="Sort all tracks by title A–Z"
                >
                  <i className="ri-text text-sm" aria-hidden />
                  Title A–Z
                </button>
                <button
                  type="button"
                  onClick={() => sortPlaylistTracks('artist')}
                  className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:border-amber-200 hover:bg-amber-50/80 hover:text-amber-900"
                  title="Sort all tracks by artist A–Z"
                >
                  <i className="ri-user-line text-sm" aria-hidden />
                  Artist A–Z
                </button>
              </>
            ) : null}
            {!onTrackSelect && playlist.id !== 0 && onContinuousPlayChange ? (
              <button
                type="button"
                onClick={() => onContinuousPlayChange(!continuousPlay)}
                aria-pressed={continuousPlay}
                title={
                  continuousPlay
                    ? 'Continuous play on: go to next track after each audio file ends'
                    : 'Continuous play off: stop after each track'
                }
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  continuousPlay
                    ? 'border-amber-300 bg-amber-50 text-amber-900 ring-1 ring-amber-200/80'
                    : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-50'
                }`}
              >
                <i className={`${continuousPlay ? 'ri-play-list-2-fill' : 'ri-play-list-2-line'} text-base`} aria-hidden />
                Continuous
              </button>
            ) : null}
            <div className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">
              {playlist.tracks.length} item{playlist.tracks.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {playlist.id === 0 ? (
          <div className="rounded-[1.5rem] bg-stone-50 px-5 py-10 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-stone-200 text-stone-500">
              <i className="ri-music-line text-2xl" />
            </div>
            <p className="mt-4 text-sm font-medium text-stone-700">No playlists yet</p>
            <p className="mt-1 text-xs text-stone-500">Create a playlist from the Manage tab.</p>
          </div>
        ) : playlist.tracks.length === 0 ? (
          <div className="rounded-[1.5rem] bg-stone-50 px-5 py-10 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-stone-200 text-stone-500">
              <i className="ri-music-line text-2xl" />
            </div>
            <p className="mt-4 text-sm font-medium text-stone-700">This playlist is empty</p>
            <p className="mt-1 text-xs text-stone-500">Add songs from Discover to build it out.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {playlist.tracks.map((track, index) => {
              const isCurrent = currentTrack.id === track.id;
              const playable = Boolean(track.audioUrl?.trim()) && getMediaType(track.audioUrl) !== 'none';
              const embedKind = getMediaType(track.audioUrl);
              const showVideoBtn =
                onPlayVideoEmbed && (embedKind === 'youtube' || embedKind === 'soundcloud');
              return (
              <div
                key={track.id}
                onDragEnter={
                  showReorder
                    ? (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    : undefined
                }
                onDragOver={
                  showReorder
                    ? (e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverIndex(index);
                      }
                    : undefined
                }
                onDrop={
                  showReorder
                    ? (e) => {
                        e.preventDefault();
                        const raw = e.dataTransfer.getData('text/plain');
                        let from = NaN;
                        if (raw.startsWith('freqloc:')) {
                          from = parseInt(raw.slice(8), 10);
                        } else {
                          from = parseInt(raw, 10);
                        }
                        if (Number.isNaN(from) && dragSourceIndexRef.current != null) {
                          from = dragSourceIndexRef.current;
                        }
                        dragSourceIndexRef.current = null;
                        if (Number.isNaN(from)) return;
                        if (from === index) return;
                        applyReorder(from, index);
                        setDraggedIndex(null);
                        setDragOverIndex(null);
                      }
                    : undefined
                }
                className={`grid items-center gap-2 rounded-[1.5rem] border px-3 py-3 transition sm:gap-3 sm:px-4 ${
                  showReorder ? 'grid-cols-[auto_auto_1fr_auto]' : 'grid-cols-[auto_1fr_auto]'
                } ${
                  isCurrent
                    ? 'border-amber-200 bg-amber-50/80'
                    : 'border-transparent bg-stone-50 hover:border-stone-200 hover:bg-white'
                } ${dragOverIndex === index && draggedIndex !== index ? 'ring-2 ring-amber-300 ring-offset-1' : ''} ${
                  draggedIndex === index ? 'opacity-60' : ''
                }`}
              >
                {showReorder ? (
                  <div
                    draggable
                    aria-label={`Drag to reorder ${track.title}`}
                    aria-grabbed={draggedIndex === index}
                    title="Drag to reorder"
                    onDragStart={(e) => {
                      e.stopPropagation();
                      dragSourceIndexRef.current = index;
                      e.dataTransfer.effectAllowed = 'move';
                      // Explicit prefix avoids empty / ambiguous reads for index 0 in some browsers.
                      e.dataTransfer.setData('text/plain', `freqloc:${index}`);
                      setDraggedIndex(index);
                    }}
                    onDragEnd={() => {
                      dragSourceIndexRef.current = null;
                      setDraggedIndex(null);
                      setDragOverIndex(null);
                    }}
                    className="flex h-9 w-9 shrink-0 cursor-grab touch-none select-none items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-white hover:text-amber-800 active:cursor-grabbing"
                  >
                    <i className="ri-drag-move-2-line text-xl" aria-hidden />
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleTrackActivate(track)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm ${
                    isCurrent ? 'bg-amber-700 text-white' : 'bg-white text-stone-700'
                  }`}
                  aria-label={isCurrent ? (isPlaying ? 'Pause' : 'Play') : `Play track ${index + 1}`}
                >
                  {!isCurrent ? (
                    <span className="text-xs font-semibold tabular-nums">{index + 1}</span>
                  ) : onTrackSelect ? (
                    <i className="ri-play-fill" />
                  ) : isPlaying ? (
                    <i className="ri-pause-mini-fill" />
                  ) : (
                    <i className="ri-play-fill" />
                  )}
                </button>
                <div className="flex min-w-0 items-center gap-3">
                  <div className="cursor-pointer shrink-0" onClick={() => handleTrackActivate(track)}>
                    <SafeImage src={track.image} alt={track.title} size="sm" />
                  </div>
                  <div className="min-w-0 cursor-pointer" onClick={() => handleTrackActivate(track)}>
                    <h3 className={`truncate text-sm font-medium ${currentTrack.id === track.id ? 'text-amber-800' : 'text-stone-900'}`}>
                      {track.title}
                    </h3>
                    <p className="truncate text-xs text-stone-500">{track.artist}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500">{track.duration}</span>
                  {!playable && !onTrackSelect ? (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-amber-800/80" title="No audio or embed URL on this track">
                      No preview
                    </span>
                  ) : null}
                  {showVideoBtn ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayVideoEmbed(track);
                      }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-stone-500 shadow-sm ring-1 ring-stone-200/80 transition-colors hover:bg-amber-50 hover:text-amber-800"
                      title="Watch video"
                      aria-label="Watch video"
                    >
                      <i className="ri-video-line text-sm" aria-hidden />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onToggleTrackLike(track.id)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${likedTrackIds[track.id] ? 'bg-red-50 text-red-500' : 'bg-white text-stone-400 hover:text-red-500'}`}
                  >
                    <i className={`${likedTrackIds[track.id] ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-sm`} />
                  </button>
                  {canRemoveTracks ? (
                    <button
                      type="button"
                      onClick={() => onRemoveTrack(playlist.id, track.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-stone-400 transition-colors hover:text-red-500"
                      aria-label="Remove track from playlist"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  ) : null}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </section>

      {playlist.id !== 0 && (
        <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-stone-900">Playlist conversation</h3>
            <p className="mt-1 text-sm text-stone-500">React to the mix and leave context for anyone listening after you.</p>
            {isLoggedIn && (
              <div className="mt-4 flex gap-2">
                <input
                  type="text"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 rounded-2xl border border-stone-200 px-4 py-3 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                />
                <button
                  type="button"
                  onClick={submitComment}
                  className="rounded-2xl bg-stone-900 px-4 py-3 text-sm font-medium text-white"
                >
                  Post
                </button>
              </div>
            )}
            {comments.length === 0 ? (
              <p className="mt-4 text-sm text-stone-500">No comments yet.</p>
            ) : (
              <div className="mt-4 space-y-3 max-h-64 overflow-y-auto pr-1">
                {comments.map((comment) => (
                  <div key={comment.id} className="rounded-[1.25rem] bg-stone-50 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                        {comment.display_name || comment.username}
                      </p>
                      {canEditPlaylistComment(comment.user_id) && editingCommentId !== comment.id && (
                        <span className="flex shrink-0 gap-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditCommentBody(comment.body);
                              setEditPlaylistModeratorNote(comment.moderator_note ?? '');
                            }}
                            className="rounded-lg p-1 text-stone-400 hover:bg-amber-50 hover:text-amber-800"
                            title="Edit"
                          >
                            <i className="ri-pencil-line text-sm" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => removePlaylistComment(comment.id)}
                            className="rounded-lg p-1 text-stone-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-sm" aria-hidden />
                          </button>
                        </span>
                      )}
                    </div>
                    {editingCommentId === comment.id ? (
                      <div className="mt-2 space-y-2">
                        <input
                          type="text"
                          value={editCommentBody}
                          onChange={(e) => setEditCommentBody(e.target.value)}
                          className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                        />
                        {showPlaylistModeratorNote() && (
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Comment by admin (optional)</label>
                            <input
                              type="text"
                              value={editPlaylistModeratorNote}
                              onChange={(e) => setEditPlaylistModeratorNote(e.target.value)}
                              placeholder="e.g. why the comment was edited"
                              className="w-full rounded-xl border border-stone-200 bg-stone-50/80 px-3 py-2 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={savePlaylistCommentEdit}
                            className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-medium text-white"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditCommentBody('');
                              setEditPlaylistModeratorNote('');
                            }}
                            className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs text-stone-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="mt-2 text-sm text-stone-700">{comment.body}</p>
                        {comment.moderator_note ? (
                          <p className="mt-2 rounded-xl border border-stone-200 bg-stone-100/80 px-3 py-2 text-xs text-stone-800">
                            <span className="font-semibold">Comment by admin:</span> {comment.moderator_note}
                          </p>
                        ) : null}
                        <CommentEditMeta
                          createdAt={comment.created_at}
                          editedAt={comment.edited_at}
                          editCount={comment.edit_count}
                          lastEditedByAdmin={comment.last_edited_by_admin}
                          className="text-xs text-stone-400 mt-1.5"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-stone-900">Partnering with</h3>
            <p className="mt-1 text-sm text-stone-500">Presented inside the Frequency Locus listening room.</p>
            <div className="mt-5 flex h-52 items-center justify-center rounded-[1.5rem] border border-stone-100 bg-[linear-gradient(180deg,#fffdf8_0%,#f5f0e5_100%)]">
              <img
                src="/logo.png"
                alt="Frequency Locus"
                className="max-h-40 w-auto max-w-full object-contain p-4"
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
